import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { login, clearError } from '../store/slices/authSlice';
import { APP_LOGO_SRC, APP_NAME, APP_TAGLINE } from '../utils/brand';

export default function Login() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
    return () => {
      dispatch(clearError());
    };
  }, [isAuthenticated, navigate, dispatch]);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(login(formData));
  };
  
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
        pb: 8, // чтобы нижние элементы (копирайт) не упирались в низ
        overflowY: 'auto',
        // Градиентный фон
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.05)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 50%,
          ${alpha(theme.palette.info.main, 0.05)} 100%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 520,
            borderRadius: 8,
            boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
            overflow: 'visible',
          }}
        >
          <CardContent
            sx={{
              p: { xs: 4, sm: 5 },
              '&:last-child': {
                pb: 8, // 64px
              },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <Box
                  component="img"
                  src={APP_LOGO_SRC}
                  alt={APP_NAME}
                  sx={{
                    width: 81,
                    height: 'auto',
                    mb: 2,
                    display: 'block',
                    objectFit: 'contain',
                  }}
                />
              </motion.div>
              
              <Typography
                variant="headlineMedium"
                component="h1"
                sx={{
                  fontWeight: 500,
                  mb: 0.5,
                  color: '#47257D',
                  fontSize: '3rem',
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                }}
              >
                {APP_NAME}
              </Typography>
              
              <Typography
                variant="bodyMedium"
                color="text.secondary"
              >
                {APP_TAGLINE}
              </Typography>
            </Box>
            
            {/* Error Alert */}
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.2 }}
              >
                <Alert 
                  severity="error" 
                  sx={{ mb: 3, borderRadius: 3 }}
                  onClose={() => dispatch(clearError())}
                >
                  {error}
                </Alert>
              </motion.div>
            )}
            
            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
              }}
            >
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                name="password"
                label="Пароль"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                {loading ? 'Вход...' : 'Войти'}
              </Button>
            </Box>
            
            {/* Footer */}
            <Typography
              variant="bodySmall"
              color="text.secondary"
              align="center"
              sx={{ mt: 4 }}
            >
              © 2024 {APP_NAME}. Все права защищены.
            </Typography>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
}
